import React from 'react';
import { Card, CardHeader, Divider, CardContent, Box, Button } from '@mui/material';
import { PatchType, FranchiseGroup, Store } from '../../../types';
import SelectedStoresSummary from '../../../features/patches/components/SelectedStoresSummary';
import PendingChangesView from '../../../features/patches/components/PendingChangesView';
import { PatchList } from '../Patches';
import { MenuViewer } from '../Menu';
import { useFeatureFlag } from '../../../services/optimizely/optimizelyHooks';
import { FeatureFlags } from '../../../constants/FeatureFlags';

interface Step3ReviewAndSubmitProps {
  selectedAction: 'review' | 'update';
  selectedStores: Store[];
  selectedFranchises: FranchiseGroup[];
  isSubmitting: boolean;
  hasFetchedPatches: boolean;
  handleRemoveOperation: () => void;
  handleSubmitOperationDrawer: () => void;
  selectedPatchType: PatchType;
  handlePatchTypeChange: (patchType: PatchType) => void;
  getPatchIdsForType?: (patchType: PatchType) => string[];
  getPatchDataForType?: (patchType: PatchType) => any[];
  hideActions?: boolean;
  removePendingOp: (index: number, type: 'set' | 'remove') => void;
  clearPendingOps: () => void;
}

const Step3ReviewAndSubmit: React.FC<Step3ReviewAndSubmitProps> = ({
  selectedAction,
  selectedStores,
  selectedFranchises,
  isSubmitting,
  hasFetchedPatches,
  handleRemoveOperation,
  handleSubmitOperationDrawer,
  selectedPatchType,
  handlePatchTypeChange,
  getPatchIdsForType,
  getPatchDataForType,
  hideActions = false,
  removePendingOp,
  clearPendingOps,
}) => {
  const { isEnabled: removeOperationsEnabled } = useFeatureFlag(FeatureFlags.REMOVE_OPERATIONS_ENABLED);
  return (
    <Card>
    <CardHeader
      title={selectedAction === 'review' ? 'Review' : 'Manage Operations'}
      action={
        selectedAction === 'update' &&
        !isSubmitting &&
        !hideActions && (
          <Box sx={{ display: 'flex', gap: 2 }} data-testid='manageOperationsActionsBox'>
            {removeOperationsEnabled && (
              <Button
                variant='outlined'
                color='error'
                onClick={handleRemoveOperation}
                disabled={selectedStores.length === 0 || !hasFetchedPatches}
              >
                Remove Operation
              </Button>
            )}
            <Button
              variant='contained'
              color='primary'
              onClick={handleSubmitOperationDrawer}
              data-testid='addOprerationButton'
            >
              Add Operation
            </Button>
          </Box>
        )
      }
    />
    <Divider />
    <CardContent>
      {selectedAction === 'review' ? (
        <Card sx={{ mb: 3 }} data-testid='reviewMenusCard'>
          <SelectedStoresSummary selectedStores={selectedStores} selectedFranchises={selectedFranchises} />
          <CardHeader title='Menu View' data-testid="menuViewHeader" />
          <Divider />
          <MenuViewer selectedStores={selectedStores} />
        </Card>
      ) : (
        <>
          <SelectedStoresSummary selectedStores={selectedStores} selectedFranchises={selectedFranchises} />
          {/* PendingChangesView: show only if there are pending ops, with debug logging */}
          {/* PendingChangesView now uses Redux state directly and handles its own delete/clear logic */}
          <PendingChangesView open={true} onClose={clearPendingOps} />
          <PatchList
            stores={selectedStores}
            storeNumbers={selectedStores.map((store) => store.storeNumber)}
            patchType={selectedPatchType}
            handlePatchTypeChange={handlePatchTypeChange}
            getPatchIdsForType={getPatchIdsForType}
            getPatchDataForType={getPatchDataForType}
          />
        </>
      )}
    </CardContent>
  </Card>
  );
};

export default Step3ReviewAndSubmit; 